import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY, LOGO_URL } from "../lib/constants";

const NAV_LINKS = [
  { id: "benefits", label: "Why Clean" },
  { id: "work", label: "Our Work" },
  { id: "testimonials", label: "Reviews" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-200/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt={COMPANY.name}
            className={`transition-all duration-300 ${
              scrolled || open ? "h-10" : "h-12"
            } w-auto ${scrolled || open ? "" : "drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"}`}
          />
          <span
            className={`hidden sm:block font-heading font-bold tracking-tight text-sm sm:text-base transition-colors ${
              scrolled || open ? "text-[#0A192F]" : "text-white"
            }`}
          >
            Y&Y Cleaning
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-${link.id}-link`}
              onClick={() => handleNav(link.id)}
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? "text-slate-700 hover:text-[#00B4D8]"
                  : "text-white/90 hover:text-[#00B4D8]"
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-btn"
            href={COMPANY.smsLink}
            className="hidden md:inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0096C7] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-cyan-500/40"
          >
            <Phone className="w-4 h-4" />
            <span>Get A Free Estimate</span>
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              scrolled || open ? "text-[#0A192F]" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-2">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-${link.id}-link`}
                onClick={() => handleNav(link.id)}
                className="text-left py-3 px-2 rounded-md text-slate-700 font-medium hover:bg-slate-50"
              >
                {link.label}
              </button>
            ))}
            <a
              data-testid="mobile-nav-call-btn"
              href={COMPANY.smsLink}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#00B4D8] hover:bg-[#0096C7] text-white px-5 py-3 rounded-full text-sm font-semibold"
            >
              <Phone className="w-4 h-4" />
              Get A Free Estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
